import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MenuShell from '../components/MenuShell';
import { ApiError } from '../services/apiClient';
import { getMyAlias, setMyAlias } from '../features/leaderboard/leaderboardApi';
import './ProfileSettings.css';

/** Lets a student set/edit the pseudonymous alias shown on instructor leaderboards (US7, FR-049). */
function ProfileSettings() {
  const { t } = useTranslation();
  const [alias, setAlias] = useState('');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getMyAlias()
      .then((result) => {
        // Don't overwrite anything the student already started typing.
        if (!cancelled && result.alias) {
          setAlias((current) => (current === '' ? (result.alias ?? '') : current));
        }
      })
      .catch(() => {
        // The form stays usable with an empty field if the saved alias can't be loaded.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSaving(true);
    setErrorMessage(null);
    setStatusMessage(null);
    try {
      await setMyAlias(alias.trim());
      setStatusMessage(t('profileSettings.saved'));
    } catch (error) {
      setErrorMessage(error instanceof ApiError ? error.message : t('profileSettings.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <MenuShell courseId="first" />
      <main className="profile-settings">
        <header className="profile-settings__hero">
          <h1>{t('profileSettings.title')}</h1>
          <p className="profile-settings__tagline">{t('profileSettings.tagline')}</p>
        </header>

        <form className="profile-settings__form" onSubmit={handleSubmit}>
          <label htmlFor="alias-input">{t('profileSettings.aliasLabel')}</label>
          <input
            id="alias-input"
            type="text"
            value={alias}
            onChange={(event) => setAlias(event.target.value)}
            minLength={2}
            maxLength={50}
            required
          />
          <button type="submit" disabled={isSaving}>
            {t('profileSettings.save')}
          </button>
        </form>

        {statusMessage && <p role="status">{statusMessage}</p>}
        {errorMessage && <p role="alert">{errorMessage}</p>}
      </main>
    </>
  );
}

export default ProfileSettings;
